import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { supabase } from '@/lib/supabase';
import { authService } from '../services/authService';
import type { AuthState } from '../types/auth.types';
import type { AuthChangeEvent, Session } from '@supabase/supabase-js';

interface AuthActions {
  initialize: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
  setError: (error: string | null) => void;
  clearError: () => void;
  clearPasswordRecoveryMode: () => void;
  syncAuthState: () => Promise<void>;
  validateSession: () => Promise<boolean>;
}

type AuthStore = AuthState & AuthActions;

// Storage keys
const STORAGE_KEY = 'nailedit-auth-storage';
const SYNC_EVENT = 'nailedit-auth-sync';
const SIGNOUT_EVENT = 'nailedit-auth-signout';

// Track session check interval globally
let sessionCheckInterval: NodeJS.Timeout | null = null;

// Track active auth subscription
let authSubscription: { data: { subscription: { unsubscribe: () => void } } } | null = null;

// Track if sign-out is in progress in THIS tab
let isSigningOutLocally = false;

// Debounce tracking for sign out
let lastSignOutTime = 0;
const SIGNOUT_DEBOUNCE_MS = 500;

// Helper to check if session is expired or about to expire (within 60 seconds)
const isSessionExpired = (session: Session | null): boolean => {
  if (!session) return true;

  const expiresAt = session.expires_at;
  if (!expiresAt) return false;

  const now = Math.floor(Date.now() / 1000);
  const bufferSeconds = 60;

  return expiresAt - now < bufferSeconds;
};

// Helper to force clear Supabase session from storage
const forceCleanSupabaseSession = () => {
  try {
    const keys = Object.keys(localStorage);
    const supabaseKeys = keys.filter(
      (key) => key.startsWith('sb-') || key.includes('supabase.auth.token')
    );

    supabaseKeys.forEach((key) => {
      localStorage.removeItem(key);
    });
  } catch (error) {
    console.error('Error cleaning Supabase session:', error);
  }
};

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => {
      // Helper function to clear session check interval
      const clearSessionCheckInterval = () => {
        if (sessionCheckInterval) {
          clearInterval(sessionCheckInterval);
          sessionCheckInterval = null;
        }
      };

      // Helper function to handle sign out
      const handleSignOut = async (
        initiatedLocally: boolean = false,
        skipNavigation: boolean = false
      ) => {
        // Debounce - prevent multiple rapid calls
        const now = Date.now();
        if (!initiatedLocally && now - lastSignOutTime < SIGNOUT_DEBOUNCE_MS) {
          return;
        }
        lastSignOutTime = now;

        try {
          // Clear the session check interval
          clearSessionCheckInterval();

          // Clear state
          set({
            user: null,
            session: null,
            supabaseUser: null,
            error: null,
            isLoading: false,
          });

          // Clear persisted storage
          if (typeof window !== 'undefined') {
            window.localStorage.removeItem(STORAGE_KEY);
          }

          // Force clean Supabase session
          forceCleanSupabaseSession();

          // Navigate if this is from a remote sign out
          if (!initiatedLocally && !skipNavigation && typeof window !== 'undefined') {
            setTimeout(() => {
              window.location.href = '/login';
            }, 150);
          }
        } catch (error) {
          console.error('Error in handleSignOut:', error);

          // Emergency cleanup
          set({
            user: null,
            session: null,
            supabaseUser: null,
            error: null,
            isLoading: false,
          });

          if (typeof window !== 'undefined') {
            window.localStorage.removeItem(STORAGE_KEY);
          }

          forceCleanSupabaseSession();

          if (!initiatedLocally && !skipNavigation) {
            setTimeout(() => {
              window.location.href = '/login';
            }, 150);
          }
        }
      };

      // Helper to load user profile
      const loadUserProfile = async (authUserId: string) => {
        try {
          const user = await authService.fetchUserProfile(authUserId);

          if (user) {
            set({
              user,
              error: null,
            });

            // Broadcast to other tabs
            if (typeof window !== 'undefined') {
              window.localStorage.setItem(SYNC_EVENT, Date.now().toString());
            }
          }
        } catch (error) {
          console.error('Error loading user profile:', error);
        }
      };

      // Helper function to set up listeners
      const setupListeners = () => {
        // Clear any existing interval first
        clearSessionCheckInterval();

        // Unsubscribe from previous auth listener if exists
        if (authSubscription) {
          authSubscription.data.subscription.unsubscribe();
          authSubscription = null;
        }

        // Set up auth state change listener
        authSubscription = supabase.auth.onAuthStateChange(
          (event: AuthChangeEvent, session: Session | null) => {
            // Only synchronous state updates here
            // All async work must be deferred outside this callback

            if (event === 'SIGNED_IN' && session) {
  set({
    session,
    supabaseUser: session.user,
  });

  // Don't fetch profile if in password recovery mode
  const currentState = get();
  if (!currentState.isPasswordRecoveryMode) {
    // Defer async profile fetching
    setTimeout(() => {
      loadUserProfile(session.user.id);
    }, 0);
  }
} else if (event === 'SIGNED_OUT') {
              if (isSigningOutLocally) {
                return;
              }

              // Defer the full sign out handler
              setTimeout(() => {
                handleSignOut(false);
              }, 0);

              // Broadcast to other tabs
              if (typeof window !== 'undefined') {
                window.localStorage.setItem(SIGNOUT_EVENT, Date.now().toString());
              }
            } else if (event === 'TOKEN_REFRESHED' && session) {
              set({ session, supabaseUser: session.user });

              if (typeof window !== 'undefined') {
                window.localStorage.setItem(SYNC_EVENT, Date.now().toString());
              }
            } else if (event === 'USER_UPDATED' && session) {
              set({ session, supabaseUser: session.user });
            } else if (event === 'PASSWORD_RECOVERY' && session) {
  // Set recovery mode - don't fetch user profile yet
  set({
    session,
    supabaseUser: session.user,
    isLoading: false,
    isPasswordRecoveryMode: true,
  });

  // Redirect to reset password page if not already there
  if (typeof window !== 'undefined' && !window.location.pathname.includes('reset-password')) {
    setTimeout(() => {
      window.location.href = '/reset-password';
    }, 100);
  }
}
          }
        );

        // Listen for storage events (cross-tab sync)
        if (typeof window !== 'undefined') {
          const handleStorageChange = async (e: StorageEvent) => {
            const currentState = get();

            if (e.key === SYNC_EVENT) {
              get().syncAuthState();
            } else if (e.key === SIGNOUT_EVENT) {
              if (currentState.session) {
                await handleSignOut(false);
              }
            } else if (e.key === STORAGE_KEY && e.newValue === null) {
              if (currentState.session) {
                await handleSignOut(false);
              }
            }
          };

          window.addEventListener('storage', handleStorageChange);
        }

        // Set up periodic session validation
        const state = get();
        if (state.session) {
          sessionCheckInterval = setInterval(() => {
            const currentState = get();
            if (currentState.session && currentState.isInitialized) {
              get().validateSession();
            } else {
              clearSessionCheckInterval();
            }
          }, 30000);
        }
      };

      return {
        // Initial state
        user: null,
        session: null,
        supabaseUser: null,
        isLoading: false,
        isInitialized: false,
        isPasswordRecoveryMode: false,
        error: null,

        // Validate current session
        validateSession: async () => {
          try {
            const state = get();

            if (!state.session) {
              clearSessionCheckInterval();
              return false;
            }

            if (isSessionExpired(state.session)) {
              try {
                const refreshPromise = supabase.auth.refreshSession();
                const timeoutPromise = new Promise<never>((_, reject) =>
                  setTimeout(() => reject(new Error('Session refresh timeout')), 10000)
                );

                const result = await Promise.race([refreshPromise, timeoutPromise]);
const { data, error } = result as { data: { session: Session | null }; error: Error | null };

                if (error) {
                  if (
                    error.message.includes('Invalid Refresh Token') ||
                    error.message.includes('refresh_token_not_found')
                  ) {
                    await handleSignOut(false);

                    if (typeof window !== 'undefined') {
                      window.localStorage.setItem(SIGNOUT_EVENT, Date.now().toString());
                    }

                    return false;
                  }

                  return false;
                }

                const session = data?.session;

                if (!session) {
                  clearSessionCheckInterval();
                  set({
                    user: null,
                    session: null,
                    supabaseUser: null,
                  });
                  return false;
                }

                set({ session, supabaseUser: session.user });

                if (typeof window !== 'undefined') {
                  window.localStorage.setItem(SYNC_EVENT, Date.now().toString());
                }

                return true;
              } catch {
                console.warn('Session refresh timed out, will retry on next cycle');
                return false;
              }
            }

            return true;
          } catch (error) {
            console.error('Session validation error:', error);
            return false;
          }
        },

        // Sync auth state from Supabase
        syncAuthState: async () => {
          try {
            if (isSigningOutLocally) {
              return;
            }

            const sessionPromise = supabase.auth.getSession();
            const timeoutPromise = new Promise<never>((_, reject) =>
              setTimeout(() => reject(new Error('Get session timeout')), 10000)
            );

            let sessionResult: { data: { session: Session | null }; error: Error | null };
try {
  const result = await Promise.race([sessionPromise, timeoutPromise]);
  sessionResult = result as { data: { session: Session | null }; error: Error | null };
} catch {
  console.warn('syncAuthState: getSession timed out');
  return;
}

            const {
              data: { session },
              error: sessionError,
            } = sessionResult;

            if (sessionError || !session) {
              clearSessionCheckInterval();
              set({
                user: null,
                session: null,
                supabaseUser: null,
              });
              return;
            }

            // Check our local state
            const currentState = get();
            if (!currentState.session && !currentState.user) {
              return;
            }

            let activeSession = session;

            if (isSessionExpired(activeSession)) {
              try {
                const refreshPromise = supabase.auth.refreshSession();
                const refreshTimeout = new Promise<never>((_, reject) =>
                  setTimeout(() => reject(new Error('Refresh timeout')), 10000)
                );

                const refreshResult = await Promise.race([refreshPromise, refreshTimeout]);
                const { data, error: refreshError } = refreshResult as { data: { session: Session | null }; error: Error | null };

                if (refreshError || !data?.session) {
                  if (
                    refreshError?.message.includes('Invalid Refresh Token') ||
                    refreshError?.message.includes('refresh_token_not_found')
                  ) {
                    await handleSignOut(false);
                    if (typeof window !== 'undefined') {
                      window.localStorage.setItem(SIGNOUT_EVENT, Date.now().toString());
                    }
                  }
                  set({
                    user: null,
                    session: null,
                    supabaseUser: null,
                  });
                  return;
                }

                activeSession = data.session;
              } catch {
                console.warn('syncAuthState: refresh timed out');
                return;
              }
            }

            // Fetch user profile
            try {
              const user = await authService.fetchUserProfile(activeSession.user.id);

              if (user) {
                set({
                  user,
                  session: activeSession,
                  supabaseUser: activeSession.user,
                });
              } else {
                clearSessionCheckInterval();
                set({
                  user: null,
                  session: null,
                  supabaseUser: null,
                });
              }
            } catch {
              console.warn('syncAuthState: user data fetch timed out');
            }
          } catch (error) {
            console.error('Sync auth state error:', error);
          }
        },

        // Initialize auth state
        initialize: async () => {
          try {
            // Don't initialize if signing out
            if (isSigningOutLocally) {
              set({
                user: null,
                session: null,
                supabaseUser: null,
                isLoading: false,
                isInitialized: true,
              });
              return;
            }

            set({ isLoading: true });

            // Reset the sign-out flag
            isSigningOutLocally = false;

            // Check Supabase session
            const { session } = await authService.getSession();

            if (!session) {
              set({
                user: null,
                session: null,
                supabaseUser: null,
                isLoading: false,
                isInitialized: true,
              });

              setupListeners();
              return;
            }

            let activeSession = session;

            if (isSessionExpired(activeSession)) {
              const {
                data: { session: refreshedSession },
                error: refreshError,
              } = await supabase.auth.refreshSession();

              if (refreshError || !refreshedSession) {
                set({
                  user: null,
                  session: null,
                  supabaseUser: null,
                  isLoading: false,
                  isInitialized: true,
                });
                setupListeners();
                return;
              }

              activeSession = refreshedSession;
            }

            // Check if we're in password recovery mode (recovery token in URL)
const isRecoveryFlow = typeof window !== 'undefined' && 
  (window.location.hash.includes('type=recovery') || 
   window.location.pathname.includes('reset-password'));

if (isRecoveryFlow) {
  // Don't fetch user profile during password recovery
  set({
    user: null,
    session: activeSession,
    supabaseUser: activeSession.user,
    isLoading: false,
    isInitialized: true,
    isPasswordRecoveryMode: true,
  });
  setupListeners();
  return;
}

// Fetch user profile for normal flow
const user = await authService.fetchUserProfile(activeSession.user.id);

if (user) {
  set({
    user,
    session: activeSession,
    supabaseUser: activeSession.user,
    isLoading: false,
    isInitialized: true,
    isPasswordRecoveryMode: false,
  });
} else {
              set({
                user: null,
                session: null,
                supabaseUser: null,
                isLoading: false,
                isInitialized: true,
              });
            }

            setupListeners();
          } catch (error) {
            console.error('Initialize auth error:', error);
            set({
              user: null,
              session: null,
              supabaseUser: null,
              isLoading: false,
              isInitialized: true,
              error: 'Failed to initialize authentication',
            });
          }
        },

        // Sign in
        signIn: async (email: string, password: string) => {
          try {
            set({ isLoading: true, error: null });

            // Reset sign-out flag
            isSigningOutLocally = false;

            const { user, error } = await authService.signIn(email, password);

            if (error) {
              set({ isLoading: false, error });
              return { error };
            }

            const { session } = await authService.getSession();

            set({
              user,
              session: session || null,
              supabaseUser: session?.user || null,
              isLoading: false,
              error: null,
            });

            if (typeof window !== 'undefined') {
              window.localStorage.setItem(SYNC_EVENT, Date.now().toString());
            }

            return { error: null };
          } catch (error) {
            console.error('Sign in error:', error);
            const errorMessage = 'An unexpected error occurred during sign in';
            set({ isLoading: false, error: errorMessage });
            return { error: errorMessage };
          }
        },

        // Sign out
        signOut: async () => {
          try {
            // Set flag to prevent SIGNED_OUT event from interfering
            isSigningOutLocally = true;

            const currentState = get();

            if (!currentState.session && !currentState.user) {
              isSigningOutLocally = false;
              forceCleanSupabaseSession();
              window.location.href = '/login';
              return;
            }

            set({ isLoading: true, error: null });

            // Step 1: Clear local state immediately
            clearSessionCheckInterval();

            set({
              user: null,
              session: null,
              supabaseUser: null,
              error: null,
              isLoading: false,
            });

            // Clear persisted storage
            if (typeof window !== 'undefined') {
              window.localStorage.removeItem(STORAGE_KEY);
            }

            // Step 2: Force clean Supabase session
            forceCleanSupabaseSession();

            // Step 3: Try Supabase API sign out (best effort)
            try {
              const localSignOutPromise = supabase.auth.signOut({ scope: 'local' });
              const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Local sign out timeout')), 1000)
              );

              await Promise.race([localSignOutPromise, timeoutPromise]);
            } catch {
              // Continue anyway
            }

            // Step 4: Broadcast to other tabs
            if (typeof window !== 'undefined') {
              window.localStorage.setItem(SIGNOUT_EVENT, Date.now().toString());
            }

            // Step 5: Brief delay
            await new Promise((resolve) => setTimeout(resolve, 100));

            // Reset flag before navigation
            isSigningOutLocally = false;

            // Step 6: Navigate
            window.location.href = '/login';
          } catch (error) {
            console.error('Sign out error:', error);

            isSigningOutLocally = false;

            // Emergency cleanup
            clearSessionCheckInterval();

            set({
              user: null,
              session: null,
              supabaseUser: null,
              isLoading: false,
              error: null,
            });

            if (typeof window !== 'undefined') {
              window.localStorage.removeItem(STORAGE_KEY);
            }

            forceCleanSupabaseSession();

            window.location.href = '/login';
          }
        },

        // Refresh user data
        refreshUser: async () => {
          try {
            const state = get();
            if (!state.supabaseUser) return;

            const user = await authService.fetchUserProfile(state.supabaseUser.id);

            if (user) {
              set({ user });
            }
          } catch (error) {
            console.error('Refresh user error:', error);
          }
        },

        setError: (error: string | null) => {
          set({ error });
        },

        clearError: () => {
          set({ error: null });
        },

        clearPasswordRecoveryMode: () => {
  set({ isPasswordRecoveryMode: false });
},
      };
    },
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        session: state.session,
      }),
    }
  )
);